import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { inputCls, btnPrimary, Field } from "./ui";

const Shell = ({ title, sub, children, testId }) => (
  <div className="flex min-h-screen items-center justify-center bg-paper px-4 py-12 dark:bg-background" data-testid={testId}>
    <Helmet><title>{title} | BalkanPuls.de</title><meta name="robots" content="noindex,nofollow" /></Helmet>
    <div className="w-full max-w-md">
      <div className="mb-8 flex justify-center"><Link to="/" aria-label="Portal"><Logo className="h-14" eager testId="auth-logo" /></Link></div>
      <div className="rounded-lg border border-border bg-card p-8 shadow-[0_1px_0_rgba(11,31,69,0.04),0_20px_40px_-24px_rgba(11,31,69,0.25)]">
        <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
        {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
        <div className="mt-6">{children}</div>
      </div>
      <p className="mt-6 text-center text-xs text-muted-foreground">Pristup samo za ovlaštene članove redakcije.</p>
    </div>
  </div>
);

const Err = ({ msg }) => (msg ? <p role="alert" className="text-sm font-medium text-destructive" data-testid="auth-error">{msg}</p> : null);
const PW_HINT = "Najmanje 12 znakova, slova i brojevi.";

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function Login() {
  const { user, setUser } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [needsSetup, setNeedsSetup] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => {
    api.get("/auth/setup-status").then((r) => setNeedsSetup(r.data.needs_setup)).catch(() => {});
  }, []);
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    if (data.mfa_required) return setMfa(data.challenge_id);
    setUser(data);
    nav(loc.state?.from || "/admin", { replace: true });
  });
  const verify = useSubmit(async () => {
    const { data } = await api.post("/auth/mfa/verify", { challenge_id: mfa, code });
    setUser(data);
    nav(loc.state?.from || "/admin", { replace: true });
  });
  if (user) return <Navigate to="/admin" replace />;
  if (mfa) return (
    <Shell title="Sigurnosni kod" sub="Poslali smo 6-cifreni kod na vašu email adresu. Vrijedi 10 minuta." testId="admin-mfa-page">
      <form onSubmit={verify.run} className="space-y-4">
        <input inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} text-center text-2xl tracking-[0.5em]`} data-testid="mfa-code-input" />
        <Err msg={verify.error} />
        <button disabled={verify.busy || code.length !== 6} className={`${btnPrimary} w-full`} data-testid="mfa-submit-button">Potvrdi</button>
      </form>
    </Shell>
  );
  return (
    <Shell title="Prijava u redakciju" sub="BalkanPuls.de urednički CMS" testId="admin-login-page">
      {needsSetup && <p className="mb-4 rounded-md bg-amber-50 p-3 text-xs text-amber-900" data-testid="setup-required-note">Sistem još nema administratora. <Link to="/admin/setup" className="font-semibold underline">Inicijalna postavka</Link></p>}
      <form onSubmit={run} className="space-y-4" data-testid="admin-login-form">
        <Field label="Email" htmlFor="l-email"><input id="l-email" type="email" autoComplete="username" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="login-email-input" /></Field>
        <Field label="Lozinka" htmlFor="l-pw"><input id="l-pw" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="login-password-input" /></Field>
        <Err msg={error} />
        <button disabled={busy} className={`${btnPrimary} w-full py-2.5`} data-testid="login-submit-button">{busy ? "Prijava…" : "Prijavi se"}</button>
        <Link to="/admin/forgot-password" className="block text-center text-sm text-muted-foreground hover:underline" data-testid="forgot-password-link">Zaboravljena lozinka?</Link>
      </form>
    </Shell>
  );
}

export function Setup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ setup_token: "", email: "", name: "", password: "" });
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/auth/setup", f);
    setUser(data);
    nav("/admin", { replace: true });
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title="Inicijalna postavka" sub="Kreiranje prvog SUPER_ADMIN računa. Potreban je jednokratni setup token iz serverske konfiguracije." testId="admin-setup-page">
      <form onSubmit={run} className="space-y-4">
        <Field label="Setup token" htmlFor="s-tok"><input id="s-tok" type="password" required value={f.setup_token} onChange={set("setup_token")} className={inputCls} data-testid="setup-token-input" /></Field>
        <Field label="Ime i prezime" htmlFor="s-name"><input id="s-name" required value={f.name} onChange={set("name")} className={inputCls} data-testid="setup-name-input" /></Field>
        <Field label="Email (ovlašteni bootstrap email)" htmlFor="s-email"><input id="s-email" type="email" required value={f.email} onChange={set("email")} className={inputCls} data-testid="setup-email-input" /></Field>
        <Field label="Lozinka" htmlFor="s-pw" hint={PW_HINT}><input id="s-pw" type="password" autoComplete="new-password" required value={f.password} onChange={set("password")} className={inputCls} data-testid="setup-password-input" /></Field>
        <Err msg={error} />
        <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="setup-submit-button">Kreiraj administratora</button>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/auth/forgot-password", { email });
    setDone(data.message);
  });
  return (
    <Shell title="Reset lozinke" sub="Unesite email svog administrativnog računa." testId="forgot-password-page">
      {done ? <p className="text-sm" data-testid="forgot-password-done">{done}</p> : (
        <form onSubmit={run} className="space-y-4">
          <Field label="Email" htmlFor="f-email"><input id="f-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email-input" /></Field>
          <Err msg={error} />
          <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="forgot-submit-button">Pošalji link</button>
        </form>
      )}
      <Link to="/admin/login" className="mt-4 block text-center text-sm text-muted-foreground hover:underline">← Nazad na prijavu</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const { busy, error, run } = useSubmit(async () => {
    await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
    setDone(true);
  });
  return (
    <Shell title="Nova lozinka" testId="reset-password-page">
      {done ? <p className="text-sm" data-testid="reset-done">Lozinka je promijenjena. Sve postojeće sesije su odjavljene. <Link to="/admin/login" className="underline">Prijavite se</Link>.</p> : (
        <form onSubmit={run} className="space-y-4">
          <Field label="Nova lozinka" htmlFor="r-pw" hint={PW_HINT}><input id="r-pw" type="password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="reset-password-input" /></Field>
          <Err msg={error} />
          <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="reset-submit-button">Sačuvaj lozinku</button>
        </form>
      )}
    </Shell>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ name: "", password: "" });
  useEffect(() => {
    api.get(`/auth/invitation/${encodeURIComponent(token)}`).then((r) => setInv(r.data)).catch(() => setInv(false));
  }, [token]);
  const { busy, error, run } = useSubmit(async () => {
    const { data } = await api.post("/auth/accept-invite", { token, ...f });
    setUser(data);
    nav("/admin", { replace: true });
  });
  return (
    <Shell title="Prihvatanje pozivnice" sub={inv ? `${inv.email} · uloga ${inv.role_code}` : ""} testId="accept-invite-page">
      {inv === false ? <p className="text-sm text-destructive" data-testid="invite-invalid">Pozivnica nije važeća ili je istekla.</p> : (
        <form onSubmit={run} className="space-y-4">
          <Field label="Ime i prezime" htmlFor="a-name"><input id="a-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="invite-name-input" /></Field>
          <Field label="Lozinka" htmlFor="a-pw" hint={PW_HINT}><input id="a-pw" type="password" autoComplete="new-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="invite-password-input" /></Field>
          <Err msg={error} />
          <button disabled={busy || !inv} className={`${btnPrimary} w-full`} data-testid="invite-submit-button">Kreiraj račun</button>
        </form>
      )}
    </Shell>
  );
}
